import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

import ProductionUnitApi from "../../services/ProductionUnitApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import SublineApi from "../../services/SublineApi";
import ProductApi from "../../services/ProductApi";
import FmeaApi from "../../services/FmeaApi";
import ReportApi from "../../services/ReportApi";

import PageHeader from "../../components/PageHeader/PageHeader";
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";

type SelectItem = { id: string; name: string };
type ProdutoType = {
  id: string;
  name?: string;
  partNumber?: string;
  familyId?: string;
  family?: any;
};
type FmeaType = { id: string; title?: string; code?: number };

const ReportFailuresByOperation: React.FC = () => {
  const { plantId } = useAuth();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translate } = useTranslation(["form", "general"]);

  const areaRef = useRef<any>(null);
  const lineRef = useRef<any>(null);
  const sublineRef = useRef<any>(null);
  const productRef = useRef<any>(null);
  const fmeaRef = useRef<any>(null);

  const [units, setUnits] = useState<SelectItem[]>([]);
  const [areas, setAreas] = useState<SelectItem[]>([]);
  const [lines, setLines] = useState<SelectItem[]>([]);
  const [sublines, setSublines] = useState<SelectItem[]>([]);

  const [selectedUnit, setSelectedUnit] = useState<SelectItem | null>(null);
  const [selectedArea, setSelectedArea] = useState<SelectItem | null>(null);
  const [selectedLine, setSelectedLine] = useState<SelectItem | null>(null);
  const [selectedSubline, setSelectedSubline] = useState<SelectItem | null>(null);

  const [products, setProducts] = useState<ProdutoType[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProdutoType | null>(null);

  const [fmeas, setFmeas] = useState<FmeaType[]>([]);
  const [selectedFmea, setSelectedFmea] = useState<FmeaType | null>(null);

  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingSublines, setLoadingSublines] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingFmeas, setLoadingFmeas] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setLoadingUnits(true);
    ProductionUnitApi.GetProductUnitByPlant(plantId)
      .then((resp) => setUnits(resp.data ?? []))
      .catch((err) =>
        displayToast("error", translate("errorLoading") as string, err?.message ?? String(err), false, 5000)
      )
      .finally(() => setLoadingUnits(false));
  }, [plantId, displayToast, translate]);

  useEffect(() => {
    if (!selectedUnit) {
      setAreas([]);
      setSelectedArea(null);
      return;
    }
    setLoadingAreas(true);
    AreaApi.GetAreaByProductionUnit(selectedUnit.id)
      .then((resp) => {
        setAreas(resp.data ?? []);
        setTimeout(() => {
          areaRef.current?.focus?.();
          areaRef.current?.show?.();
        }, 0);
      })
      .catch((err) =>
        displayToast("error", translate("errorLoading") as string, err?.message ?? String(err), false, 5000)
      )
      .finally(() => setLoadingAreas(false));
  }, [selectedUnit, displayToast, translate]);

  useEffect(() => {
    if (!selectedArea) {
      setLines([]);
      setSelectedLine(null);
      return;
    }
    setLoadingLines(true);
    LineApi.GetLinesByArea(selectedArea.id)
      .then((resp) => {
        setLines(resp.data ?? []);
        setTimeout(() => {
          lineRef.current?.focus?.();
          lineRef.current?.show?.();
        }, 0);
      })
      .catch((err) =>
        displayToast("error", translate("errorLoading") as string, err?.message ?? String(err), false, 5000)
      )
      .finally(() => setLoadingLines(false));
  }, [selectedArea, displayToast, translate]);

  useEffect(() => {
    if (!selectedLine) {
      setSublines([]);
      setSelectedSubline(null);
      return;
    }
    setLoadingSublines(true);
    SublineApi.GetSublineByLine(selectedLine.id)
      .then((resp) => {
        setSublines(resp.data ?? []);
        setTimeout(() => {
          sublineRef.current?.focus?.();
          sublineRef.current?.show?.();
        }, 0);
      })
      .catch((err) =>
        displayToast("error", translate("errorLoading") as string, err?.message ?? String(err), false, 5000)
      )
      .finally(() => setLoadingSublines(false));
  }, [selectedLine, displayToast, translate]);

  useEffect(() => {
    const load: any = async () => {
      if (!selectedLine) {
        setProducts([]);
        setSelectedProduct(null);
        return;
      }
      setLoadingProducts(true);
      try {
        const resp = await ProductApi.GetProductsByLine(selectedLine.id);
        const prods = resp?.data ?? [];
        setProducts(prods);
        if (selectedProduct && !prods.some((p: ProdutoType) => p.id === selectedProduct.id)) {
          setSelectedProduct(null);
        }
      } catch (err: any) {
        setProducts([]);
        setSelectedProduct(null);
        displayToast("error", translate("errorLoading") as string, err?.message ?? String(err), false, 5000);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLine]);

  useEffect(() => {
    const load: any = async () => {
      if (!selectedProduct || !selectedSubline) {
        setFmeas([]);
        setSelectedFmea(null);
        return;
      }

      setLoadingFmeas(true);

      try {
        const productIds = selectedProduct ? [selectedProduct.id] : [];
        const subLineIds = selectedSubline ? [selectedSubline.id] : [];

        const resp = await FmeaApi.GetForFilter({
          productIds,
          subLineIds,
        });

        const items = resp?.data ?? [];
        setFmeas(items);
        setTimeout(() => {
          fmeaRef.current?.focus?.();
          fmeaRef.current?.show?.();
        }, 0);

        if (selectedFmea && !items.some((f: any) => f.id === selectedFmea.id)) {
          setSelectedFmea(null);
        }
      } catch (err: any) {
        setFmeas([]);
        setSelectedFmea(null);
        displayToast("error", translate("errorLoading") as string, err?.message ?? String(err), false, 5000);
      } finally {
        setLoadingFmeas(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, selectedSubline]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!selectedUnit) e.unit = translate("UnitRequired");
    if (!selectedArea) e.area = translate("AreaRequired");
    if (!selectedLine) e.line = translate("LineRequired");
    if (!selectedSubline) e.subline = translate("SublineRequired");
    if (!selectedProduct) e.product = translate("ProductRequired");
    if (!selectedFmea) e.fmea = translate("FmeaRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleGenerate = async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e?.preventDefault();
    if (!validate()) {
      displayToast(
        "error",
        translate("error") as string,
        translate("requiredFieldsMissing") as string,
        false,
        5000
      );
      return;
    }

    const payload = {
      plantId: plantId,
      productionUnitId: selectedUnit!.id,
      areaId: selectedArea!.id,
      lineId: selectedLine!.id,
      sublineId: selectedSubline!.id,
      productId: selectedProduct!.id,
      fmeaId: selectedFmea!.id,
    };

    setGenerating(true);
    try {
      const resp = await ReportApi.GenerateFailuresByOperation(payload);

      const blob = new Blob([resp.data], { type: "application/pdf" });
      const fileName = `failures-report-${selectedLine!.name ?? selectedLine!.id}.pdf`;

      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      displayToast(
        "success",
        translate("Success") as string,
        translate("generatedSuccess"),
        false,
        4000
      );
    } catch (err: any) {
      try {
        const response = err?.response;
        if (response && response.data && response.headers) {
          const contentType = response.headers["content-type"] || "";
          if (contentType.includes("application/json") || contentType.includes("text/json")) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const json = JSON.parse(String(reader.result));
                const msg = json?.message ?? json?.data ?? JSON.stringify(json);
                displayToast("error", translate("error") as string, msg, false, 7000);
              } catch {
                displayToast("error", translate("error") as string, err?.message ?? String(err), false, 7000);
              }
            };
            reader.readAsText(response.data);
            return;
          }
        }
        const msg = err?.response?.data?.message ?? err?.message ?? String(err);
        displayToast("error", translate("error") as string, msg, false, 7000);
      } catch (inner) {
        displayToast("error", translate("error") as string, err?.message ?? String(err), false, 7000);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCancel = (): void => navigate(-1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <PageHeader
        title={translate("form:ReportFailuresByOperation")}
        description={translate("form:ReportFailuresByOperationDescription" as any)}
      />

      <FormCard
        title={translate("generalData")}
        icon="description"
        onSubmit={handleGenerate}
        onCancel={handleCancel}
        numColumns={6}
        submitButtonLabel={translate("form:Generate")}
        submitButtonIcon="picture_as_pdf"
        cancelButtonLabel={translate("form:Cancel")}
        disabled={generating}
      >
        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="select"
            label={translate("productionUnit")}
            required
            value={selectedUnit}
            options={units}
            optionLabel="name"
            onChange={setSelectedUnit}
            placeholder={translate("selectUnit")}
            disabled={loadingUnits}
            className={errors.unit ? "p-invalid" : ""}
          />
          {errors.unit && <small className="p-error">{errors.unit}</small>}
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="select"
            label={translate("area")}
            required
            value={selectedArea}
            options={areas}
            optionLabel="name"
            onChange={setSelectedArea}
            placeholder={translate("selectArea")}
            disabled={!selectedUnit || loadingAreas}
            ref={areaRef}
            className={errors.area ? "p-invalid" : ""}
          />
          {errors.area && <small className="p-error">{errors.area}</small>}
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="select"
            label={translate("line")}
            required
            value={selectedLine}
            options={lines}
            optionLabel="name"
            onChange={setSelectedLine}
            placeholder={translate("selectLine")}
            disabled={!selectedArea || loadingLines}
            ref={lineRef}
            className={errors.line ? "p-invalid" : ""}
          />
          {errors.line && <small className="p-error">{errors.line}</small>}
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="select"
            label={translate("subline")}
            required
            value={selectedSubline}
            options={sublines}
            optionLabel="name"
            onChange={(val) => {
              setSelectedSubline(val);
              setTimeout(() => {
                productRef.current?.focus?.();
                productRef.current?.show?.();
              }, 0);
            }}
            placeholder={translate("selectSubline")}
            disabled={!selectedLine || loadingSublines}
            ref={sublineRef}
            className={errors.subline ? "p-invalid" : ""}
          />
          {errors.subline && <small className="p-error">{errors.subline}</small>}
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="select"
            label={translate("product")}
            required
            value={selectedProduct}
            options={products}
            optionLabel="partNumber"
            onChange={setSelectedProduct}
            placeholder={translate("selectProduct")}
            disabled={!selectedLine || loadingProducts}
            ref={productRef}
            className={errors.product ? "p-invalid" : ""}
          />
          {errors.product && <small className="p-error">{errors.product}</small>}
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <Input
            type="select"
            label="FMEA"
            required
            value={selectedFmea}
            options={fmeas}
            optionLabel="title"
            onChange={setSelectedFmea}
            placeholder={translate("selectFmea")}
            disabled={!selectedProduct || !selectedSubline || loadingFmeas}
            ref={fmeaRef}
            className={errors.fmea ? "p-invalid" : ""}
          />
          {errors.fmea && <small className="p-error">{errors.fmea}</small>}
        </div>
      </FormCard>
    </div>
  );
};

export default ReportFailuresByOperation;
